/**
 * /admin/billing — unified billing aggregation tab.
 *
 * Single line-item feed across:
 *   - B2B Invoices (apps/payments/Invoice)
 *   - B2C topups (WalletTransaction reason=TOPUP_PURCHASE)
 *   - Subscription charges (WalletTransaction reason=SUBSCRIPTION_GRANT)
 *   - Credit notes (CreditNote — refunds, negative amounts)
 *
 * Filters: Range / Product (ZCD / ZCA) / Segment (B2B / B2C) / Kind /
 * State / free-text search. KPI strip on top reflects the FILTERED set
 * so operators can answer "show me everything I billed this month"
 * without leaving the page.
 *
 * Backed by GET /api/admin/v2/billing/lines/.
 */
import { useEffect, useMemo, useState } from "react";
import {
  RefreshCw, TrendingUp, ArrowDownRight, Receipt,
  AlertTriangle, Loader2, Search, Download,
} from "lucide-react";
import { listBillingLines } from "../../api/owner.api";


const RANGES = [
  { key: "mtd",  label: "Month-to-date" },
  { key: "30d",  label: "Last 30 days" },
  { key: "90d",  label: "Last 90 days" },
  { key: "ytd",  label: "Year-to-date" },
];

const PRODUCTS = [
  { key: "all", label: "All products" },
  { key: "zcd", label: "ZenCricketData" },
  { key: "zca", label: "ZenCricketAnalyzer" },
];

const SEGMENTS = [
  { key: "all", label: "All segments" },
  { key: "b2b", label: "B2B" },
  { key: "b2c", label: "B2C" },
];

const KINDS = [
  { key: "all",          label: "All kinds" },
  { key: "invoice",      label: "Invoice" },
  { key: "topup",        label: "Topup" },
  { key: "subscription", label: "Subscription" },
  { key: "credit_note",  label: "Credit note" },
];

const STATES = [
  { key: "all",      label: "Any state" },
  { key: "paid",     label: "Paid" },
  { key: "issued",   label: "Issued" },
  { key: "refunded", label: "Refunded" },
];


function fmtInr(amount) {
  if (amount == null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR", maximumFractionDigits: 0,
  }).format(amount);
}

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return iso;
  }
}


export default function AdminBilling() {
  const [range, setRange] = useState("30d");
  const [product, setProduct] = useState("all");
  const [segment, setSegment] = useState("all");
  const [kind, setKind] = useState("all");
  const [state, setState] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const params = useMemo(() => ({
    range, product, segment, kind, state, search, page, page_size: 50,
  }), [range, product, segment, kind, state, search, page]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, product, segment, kind, state, page]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await listBillingLines(params);
      setRows(res.data.results || []);
      setSummary(res.data.summary || null);
      setCount(res.data.count || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }

  const [exporting, setExporting] = useState(false);
  async function downloadCsv() {
    // Fetch the FULL filtered set (not just the current page) so the
    // accountant download matches the KPI strip. Backend caps the
    // unpaginated response at 50_000 rows to prevent runaways.
    setExporting(true);
    try {
      const res = await listBillingLines({ ...params, all: 1 });
      const allRows = res.data.results || [];
      if (!allRows.length) return;
      const header = [
        "date", "kind", "product", "segment",
        "customer_name", "customer_email_or_gstin",
        "description", "amount_inr", "tax_inr", "state", "source_id",
      ];
      const lines = [header.join(",")];
      for (const r of allRows) {
        const safe = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
        lines.push([
          r.issued_at,
          r.kind,
          r.product,
          r.segment,
          safe(r.customer?.name || ""),
          safe(r.customer?.email_or_gstin || ""),
          safe(r.description || ""),
          r.amount_inr,
          r.tax_inr,
          r.state,
          safe(r.source_ref?.id || ""),
        ].join(","));
      }
      const blob = new Blob([lines.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `zcd-billing-${range}-${allRows.length}rows.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "CSV export failed");
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Billing</h1>
          <p className="text-sm text-text-secondary mt-1">
            Unified feed of invoices, topups, subscription charges and credit notes.
            KPI strip + table reflect the filters below.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => downloadCsv()}
            disabled={!rows.length || exporting}
            title="Download the FULL filtered set as CSV (not just this page)"
            className="px-3 py-2 border border-border-default rounded hover:bg-bg-secondary inline-flex items-center gap-2 text-sm disabled:opacity-40"
          >
            {exporting
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Download className="w-4 h-4" />} CSV
          </button>
          <button
            onClick={() => load()}
            className="px-3 py-2 border border-border-default rounded hover:bg-bg-secondary inline-flex items-center gap-2 text-sm"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </header>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 items-center">
        <Select value={range} onChange={setRange} options={RANGES} />
        <Select value={product} onChange={setProduct} options={PRODUCTS} />
        <Select value={segment} onChange={setSegment} options={SEGMENTS} />
        <Select value={kind} onChange={setKind} options={KINDS} />
        <Select value={state} onChange={setState} options={STATES} />
        <div className="flex items-center gap-1 flex-1 min-w-[200px]">
          <div className="relative w-full">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-text-secondary" />
            <input
              type="text"
              value={search}
              placeholder="Customer / description / id…"
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              className="w-full pl-8 pr-3 py-2 bg-bg-primary border border-border-default rounded text-sm"
            />
          </div>
        </div>
      </div>

      {/* KPI strip */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Gross" value={fmtInr(summary.gross_inr)} icon={TrendingUp} tone="positive" />
          <Kpi label="Tax (CGST+SGST+IGST)" value={fmtInr(summary.tax_inr)} icon={Receipt} tone="neutral" />
          <Kpi label="Refunds" value={fmtInr(summary.refunds_inr)} icon={ArrowDownRight} tone="negative" />
          <Kpi label="Net" value={fmtInr(summary.net_inr)} icon={TrendingUp} tone="primary" />
        </div>
      )}
      {summary && (
        <div className="text-xs text-text-secondary flex flex-wrap gap-4">
          <span>By product: ZCD {summary.by_product?.zcd || 0} · ZCA {summary.by_product?.zca || 0}</span>
          <span>By segment: B2B {summary.by_segment?.b2b || 0} · B2C {summary.by_segment?.b2c || 0}</span>
          <span>Showing {rows.length} of {count}</span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/40 rounded-md p-3 text-sm text-rose-200 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          {error}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto border border-border-default rounded-md">
        <table className="w-full text-sm">
          <caption className="sr-only">Billing line items</caption>
          <thead className="bg-bg-secondary text-text-secondary">
            <tr>
              <Th>Date</Th>
              <Th>Kind</Th>
              <Th>Customer</Th>
              <Th>Description</Th>
              <Th>Product</Th>
              <Th>Segment</Th>
              <Th className="text-right">Amount</Th>
              <Th>State</Th>
              <Th>PDF</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="p-4 text-center text-text-secondary"><Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="p-4 text-center text-text-secondary">No billing lines for the selected filters.</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="border-t border-border-default hover:bg-bg-secondary/50">
                  <Td className="whitespace-nowrap">{fmtDate(r.issued_at)}</Td>
                  <Td>
                    <KindBadge kind={r.kind} />
                  </Td>
                  <Td>
                    <div className="text-text-primary">{r.customer?.name || "—"}</div>
                    <div className="text-xs text-text-secondary">{r.customer?.email_or_gstin || ""}</div>
                  </Td>
                  <Td className="max-w-md truncate" title={r.description}>{r.description || "—"}</Td>
                  <Td className="uppercase text-xs">{r.product}</Td>
                  <Td className="uppercase text-xs">{r.segment}</Td>
                  <Td className={`text-right font-mono ${r.amount_inr < 0 ? "text-rose-300" : "text-text-primary"}`}>{fmtInr(r.amount_inr)}</Td>
                  <Td><StateBadge state={r.state} /></Td>
                  <Td>
                    {r.source_ref?.pdf_url
                      ? <span aria-label="PDF available">✓</span>
                      : <span aria-label="No PDF">—</span>}
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center text-sm">
        <div className="text-text-secondary">Page {page} of {Math.max(1, Math.ceil(count / 50))}</div>
        <div className="flex gap-2">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1 || loading} className="px-3 py-1 border border-border-default rounded hover:bg-bg-secondary disabled:opacity-40">Prev</button>
          <button onClick={() => setPage((p) => p + 1)} disabled={page * 50 >= count || loading} className="px-3 py-1 border border-border-default rounded hover:bg-bg-secondary disabled:opacity-40">Next</button>
        </div>
      </div>
    </div>
  );
}


function Select({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-bg-primary border border-border-default rounded px-2 py-2 text-sm text-text-primary"
    >
      {options.map((o) => (
        <option key={o.key} value={o.key}>{o.label}</option>
      ))}
    </select>
  );
}

function Kpi({ label, value, icon: Icon, tone }) {
  const colorClasses = {
    positive: "text-emerald-300 border-emerald-500/40 bg-emerald-500/5",
    negative: "text-rose-300 border-rose-500/40 bg-rose-500/5",
    primary:  "text-indigo-300 border-indigo-500/40 bg-indigo-500/5",
    neutral:  "text-text-primary border-border-default bg-bg-secondary",
  }[tone] || "text-text-primary border-border-default bg-bg-secondary";
  return (
    <div className={`border rounded-md p-3 ${colorClasses}`}>
      <div className="text-xs uppercase tracking-wide flex items-center gap-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-lg font-semibold mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th scope="col" className={`text-left px-3 py-2 font-medium ${className}`}>
      {children}
    </th>
  );
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2 ${className}`}>{children}</td>;
}

function KindBadge({ kind }) {
  const map = {
    invoice:      ["bg-indigo-500/15 text-indigo-200", "Invoice"],
    topup:        ["bg-emerald-500/15 text-emerald-200", "Topup"],
    subscription: ["bg-sky-500/15 text-sky-200", "Subscription"],
    credit_note:  ["bg-rose-500/15 text-rose-200", "Credit note"],
  };
  const [cls, label] = map[kind] || ["bg-bg-secondary text-text-secondary", kind];
  return <span className={`px-2 py-0.5 rounded text-xs ${cls}`}>{label}</span>;
}
function StateBadge({ state }) {
  const map = {
    paid:     ["bg-emerald-500/15 text-emerald-200", "Paid"],
    issued:   ["bg-amber-500/15 text-amber-200", "Issued"],
    refunded: ["bg-rose-500/15 text-rose-200", "Refunded"],
  };
  const [cls, label] = map[state] || ["bg-bg-secondary text-text-secondary", state];
  return <span className={`px-2 py-0.5 rounded text-xs ${cls}`}>{label}</span>;
}
